import { Component, OnInit, DestroyRef, inject, HostListener } from '@angular/core';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@core/auth/auth.service';

import { filter } from 'rxjs';
import { SidebarComponent } from '@presentation/shared/components/sidebar/sidebar.component';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SECTION_CLASSES, PAGE_TITLES, PAGE_ICONS, getSectionClass } from '@core/routing/section.utils';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: true,
  imports: [RouterModule, SidebarComponent, MatIconModule, MatButtonModule, MatTooltipModule],
})
export class AppComponent implements OnInit {

  // Données de l'utilisateur connecté
  roles:    string[] = [];
  username: string   = '';
  role:     string   = '';

  // État de l'interface
  sidebarCollapsed = false;
  isDarkTheme      = false;

  // Topbar : titre et icône de la page courante
  pageTitle = '';
  pageIcon  = '';

  // true quand l'URL active est une page d'authentification (login, register)
  // → utilisé dans le template pour masquer la sidebar et la topbar
  isAuthRoute = false;

  // takeUntilDestroyed gère automatiquement le unsubscribe quand le composant
  // est détruit — plus besoin de ngOnDestroy + unsubscribe() manuels
  private destroyRef = inject(DestroyRef);

  constructor(private authService: AuthService, private router: Router) {
    // Initialisation des données utilisateur depuis le token JWT stocké
    const decodedToken  = this.authService.getDecodedToken();
    this.roles    = this.authService.getRoles();
    this.username = decodedToken?.sub         || '';
    this.role     = decodedToken?.roles?.[0]  || '';

    // Mise à jour en temps réel si le token change (connexion / déconnexion)
    this.authService.userState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(token => {
        this.roles    = token?.roles    || [];
        this.username = token?.sub      || '';
        this.role     = token?.roles?.[0] || '';
      });
  }

  ngOnInit(): void {
    // Restaurer le thème choisi par l'utilisateur lors de sa dernière session
    if (localStorage.getItem('theme') === 'dark') {
      this.isDarkTheme = true;
      document.body.classList.add('dark-theme');
    }

    // Appliquer la section dès le chargement initial (page rafraîchie)
    this.updateRoute(this.router.url);

    // Mettre à jour la section à chaque navigation
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => this.updateRoute((event as NavigationEnd).urlAfterRedirects));
  }

  // ─── Actions ──────────────────────────────────────────────────────────────

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  toggleTheme(): void {
    this.isDarkTheme = !this.isDarkTheme;
    document.body.classList.toggle('dark-theme', this.isDarkTheme);
    localStorage.setItem('theme', this.isDarkTheme ? 'dark' : 'light');
  }

  logout(): void {
    this.authService.logout();
  }

  // ─── Privé ────────────────────────────────────────────────────────────────

  // ⚡ Konami Code: ↑↑↓↓←→←→BA
  private konamiSequence = ['ArrowUp','ArrowUp','ArrowDown','ArrowDown','ArrowLeft','ArrowRight','ArrowLeft','ArrowRight','b','a'];
  private konamiIndex = 0;

  @HostListener('window:keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if (e.key === this.konamiSequence[this.konamiIndex]) {
      this.konamiIndex++;
      if (this.konamiIndex === this.konamiSequence.length) {
        this.konamiIndex = 0;
        this.triggerKonami();
      }
    } else {
      this.konamiIndex = e.key === this.konamiSequence[0] ? 1 : 0;
    }
  }

  private triggerKonami(): void {
    const emojis = ['⚡','🔥','🎉','✨','🚀','💎','🌈','⭐','🦖'];
    for (let i = 0; i < 50; i++) {
      const el = document.createElement('div');
      el.textContent = emojis[Math.floor(Math.random() * emojis.length)];
      Object.assign(el.style, {
        position: 'fixed', top: '-40px', fontSize: `${20 + Math.random() * 20}px`,
        left: `${Math.random() * 100}vw`, zIndex: '99999', pointerEvents: 'none',
        transition: `transform ${2 + Math.random() * 3}s linear, opacity ${2 + Math.random() * 3}s ease`,
        transform: 'translateY(0)', opacity: '1',
      });
      document.body.appendChild(el);
      requestAnimationFrame(() => {
        el.style.transform = `translateY(${window.innerHeight + 60}px) rotate(${Math.random() * 720 - 360}deg)`;
        el.style.opacity = '0';
      });
      setTimeout(() => el.remove(), 5500);
    }
    console.log('%c⚡ KONAMI CODE ACTIVATED ⚡', 'color: #ff9800; font-size: 24px; font-weight: bold; text-shadow: 2px 2px #000;');
  }

  // Met à jour isAuthRoute, la classe CSS sur <body> et les infos de la topbar
  private updateRoute(url: string): void {
    this.isAuthRoute = url.startsWith('/auth');

    // Retirer l'ancienne classe de section puis appliquer la nouvelle
    SECTION_CLASSES.forEach(c => document.body.classList.remove(c));
    const section = getSectionClass(url);
    if (section) {
      document.body.classList.add(section);
      this.pageTitle = PAGE_TITLES[section];
      this.pageIcon  = PAGE_ICONS[section];
    } else {
      this.pageTitle = '';
      this.pageIcon  = '';
    }
  }
}
